// Server-side backups client (the Backup / Restore tab). Backups are dumped to
// the server and tracked in server_backups; the tab lists / restores / deletes
// them. Take + restore are async (a one-shot progress token polled via
// lib/progress.js::getProgress, same as the download backup / upload restore).
import { api } from './api.js';
import { triggerDownload } from './export.js';

// List the caller's visible backups (admin: all; others: own).
export async function listServerBackups() {
  const r = await api.get('/api/server-backups');
  return r?.backups || [];
}

// List in-flight (+ recently finished) backup/restore operations for the
// "Active operations" table — scoped like the backups list. Each entry carries
// the progress fields (phase, current, total, …) + the token (to resolve a
// paused restore) + kind/target/startedAt.
export async function listServerOperations() {
  const r = await api.get('/api/server-backups/operations');
  return r?.operations || [];
}

// Take a backup → save it on the server. `spec`:
//   { connectionId, database, note, data, triggers, routines, views, events,
//     stripDefiner, gzip, singleTransaction }
// Returns the progress token.
export async function createServerBackup(spec) {
  const r = await api.post('/api/server-backups', spec);
  return r.token;
}

// Restore a stored backup into a target connection/database. `body`:
//   { connectionId, database, createDb, retarget, autoTune, postSql }
// Returns { token, total } for the progress poll.
export async function restoreServerBackup(id, body) {
  return api.post(`/api/server-backups/${id}/restore`, body);
}

// Delete a stored backup (row + file). Gated server-side on allow_delete_backup.
export async function deleteServerBackup(id) {
  return api.delete(`/api/server-backups/${id}`);
}

// Bulk-delete several backups by id (row + file each). Same gate as single
// delete (allow_delete_backup; own-only for non-admins). Returns { deleted, freedBytes }.
export async function bulkDeleteServerBackups(ids) {
  return api.post('/api/server-backups/bulk-delete', { ids });
}

// Download a stored backup file via a streaming GET (cookie-auth, same origin).
// Uses the export download trick (an <a download>) so the SPA router doesn't
// swallow the navigation.
export function downloadServerBackup(id, filename) {
  triggerDownload(`/api/server-backups/${id}/download`, filename || 'backup.sql');
}

// Admin-only bulk prune: delete the given backups (row + file each). The caller
// computes the id set (e.g. older than N days within the active filters);
// olderThanDays is sent for the audit log only. Returns { deleted, freedBytes }.
export async function pruneServerBackups(ids, olderThanDays) {
  return api.post('/api/server-backups/prune', { ids, olderThanDays });
}

// Storage readout for the Backup/Restore tab.
//   admin     → { isAdmin:true,  storeSizeBytes, diskFreeBytes, diskTotalBytes, diskFreePercent }
//   non-admin → { isAdmin:false, ownSizeBytes }
export async function getServerBackupStorage() {
  return api.get('/api/server-backups/storage');
}
